import { Box, Grid } from "@material-ui/core";
import { assetIds, InformationMessage, ThemedAsset } from "nemuru-components";
import React, { useContext, useEffect, useState } from "react";
import { ICON_TITLE_HEIGHT } from "../../../../config/config";
import { ProcessContext } from "../../../../contexts/ProcessContext";
import { convertToBase64 } from "../../../../utils/convertToBase64";
import IconTitle from "../../../shared/components/IconTitle";
import MainButton from "../../../shared/components/MainButton";
import Text from "../../../shared/components/Text";
import DocumentationRequired from "./components/DocumentationRequired";
import DocumentRequiredDialog from "./components/DocumentRequiredDialog";
import { useTheme } from "@material-ui/styles";
import { documentStatus } from "./config";

const TIMEOUT_UPLOADED_FILES_MILLISECONDS = 1500;
const TIMEOUT_MILLISECONDS = 5000;
const UPLOADED_FILE_STATUS = [
  documentStatus.UPLOADED,
  documentStatus.VALIDATED,
];

export default function Documentation() {
  const {
    state: {
      loan,
      editableDocumentationRequired,
      lastActionsReference,
      lastActionsReferenceLastChange,
      participant,
      documentationSyncedAfterSigning,
    },
    dispatch,
  } = useContext(ProcessContext);
  const { palette } = useTheme();

  const [state, setState] = useState({
    open: false,
    files: [],
    fadeOut: false,
    document: undefined,
    documentation: undefined,
    filesUploading: false,
    timer: TIMEOUT_MILLISECONDS,
    countDocumentsWithFiles: undefined,
    countAllDocuments: undefined,
    confirmDocumentationClicked: false,
    areRequiredFilesUploaded: false,
    documentationSync: false,
  });

  const {
    open,
    files,
    fadeOut,
    document,
    documentation,
    filesUploading,
    countDocumentsWithFiles,
    countAllDocuments,
    confirmDocumentationClicked,
    areRequiredFilesUploaded,
    documentationSync,
  } = state;

  const isActionTypeInLastActionsReference = (actionType) =>
    lastActionsReference?.some((element) => element.type === actionType);

  useEffect(() => {
    if (documentationSyncedAfterSigning) {
      setState((s) => ({ ...s, documentationSync: true }));
      checkIfDocumentsAreConfirmedAndTakeAction();
      return;
    }

    if (loan) {
      patchSyncBorrowerDocumentationByLoanId();
      setTimeout(
        () =>
          dispatch({
            type: "SET_FLOW_LOADING",
            payload: {
              show: true,
              loadingMessage: <Text tId={"DOCUMENTATION_SYNC"} />,
            },
          }),
        200
      );
    }
  }, []);

  const patchSyncBorrowerDocumentationByLoanId = () => {
    dispatch({
      type: "REQUEST_PATCH_SYNC_BORROWER_DOCUMENTATION_BY_LOAN_ID",
      payload: {
        op: "processPatchSyncBorrowerDocumentationByLoanId",
        id: loan.id,
      },
    });
  };

  useEffect(() => {
    if (
      isActionTypeInLastActionsReference(
        "REQUEST_PATCH_SYNC_BORROWER_DOCUMENTATION_BY_LOAN_ID_FULFILLED"
      ) ||
      isActionTypeInLastActionsReference(
        "REQUEST_PATCH_SYNC_BORROWER_DOCUMENTATION_BY_LOAN_ID_REJECTED"
      )
    ) {
      getDocumentationRequiredByLoanIdAndBorrowerId();
      setState((s) => ({ ...s, documentationSync: true }));
    }
  }, [lastActionsReferenceLastChange]);

  const getDocumentationRequiredByLoanIdAndBorrowerId = () => {
    dispatch({
      type: "REQUEST_GET_DOCUMENTATION_REQUIRED",
      payload: { loanId: loan.id, borrowerId: participant.participantId },
    });
  };

  useEffect(() => {
    if (documentationSync === true && documentation) {
      dispatch({
        type: "SET_FLOW_LOADING",
        payload: {
          show: false,
        },
      });
      setState((s) => ({
        ...s,
        areRequiredFilesUploaded: areRequiredDocumentsUploaded(documentation),
        countDocumentsWithFiles: countDocumentsWithUploadedFiles(documentation),
        countAllDocuments: documentation?.required_files?.length,
      }));
    }
  }, [documentation]);

  const countDocumentsWithUploadedFiles = (documentationToCountFiles) => {
    let documentsWithUploadedFiles = 0;
    documentationToCountFiles?.required_files?.map((required_file) => {
      if (UPLOADED_FILE_STATUS.includes(required_file.status)) {
        documentsWithUploadedFiles = documentsWithUploadedFiles + 1;
      }
    });
    return documentsWithUploadedFiles;
  };

  const areRequiredDocumentsUploaded = (documentationToCheck) => {
    let requiredDocumentsUploaded = true;
    documentationToCheck.required_files.map((requiredFile) => {
      if (!UPLOADED_FILE_STATUS.includes(requiredFile.status)) {
        requiredDocumentsUploaded = false;
      }
    });
    return requiredDocumentsUploaded;
  };

  useEffect(() => {
    if (editableDocumentationRequired && confirmDocumentationClicked) {
      if (areRequiredFilesUploaded === true) {
        dispatch({
          type: "REQUEST_PATCH_SET_DOCUMENTS_CONFIRMED",
          payload: {
            op: "processPatchSetAreDocumentsConfirmed",
            id: editableDocumentationRequired.id,
            are_documents_confirmed: true,
          },
        });
      } else {
        setTimeout(() => handleConfirmDocuments(), TIMEOUT_MILLISECONDS);
        setState((s) => ({
          ...s,
          timer: s.timer + TIMEOUT_MILLISECONDS,
        }));
      }
    }
  }, [editableDocumentationRequired]);

  useEffect(() => {
    if (
      isActionTypeInLastActionsReference(
        "REQUEST_UPLOAD_BORROWER_DOCUMENTATION_FULFILLED"
      )
    ) {
      setState((s) => ({
        ...s,
        documentation: addFilesToDocumentation(documentation, files),
        files: [],
        filesUploading: false,
        document: {
          ...document,
          status: documentStatus.UPLOADED,
          uploaded_files: [
            ...document.uploaded_files,
            ...files.map((file) => {
              return { name: file.name, mime_type: file.mime_type };
            }),
          ],
        },
      }));
      setTimeout(() => {
        handleClose();
      }, TIMEOUT_UPLOADED_FILES_MILLISECONDS);
    }
    if (
      isActionTypeInLastActionsReference(
        "REQUEST_PATCH_SET_DOCUMENTS_CONFIRMED"
      )
    ) {
      setFormSubmitSuccess();
    }
    if (
      isActionTypeInLastActionsReference(
        "REQUEST_GET_DOCUMENTATION_REQUIRED_FULFILLED"
      )
    ) {
      checkIfDocumentsAreConfirmedAndTakeAction();
    }
  }, [lastActionsReferenceLastChange]);

  const checkIfDocumentsAreConfirmedAndTakeAction = () => {
    const requiredFilesUploaded = areRequiredDocumentsUploaded(
      editableDocumentationRequired
    );

    if (
      editableDocumentationRequired?.required_files.length === 0 ||
      (requiredFilesUploaded &&
        editableDocumentationRequired.are_documents_confirmed &&
        !confirmDocumentationClicked)
    ) {
      onSkipForm();
    } else {
      setState((s) => ({
        ...s,
        documentation: { ...editableDocumentationRequired },
        areRequiredFilesUploaded: requiredFilesUploaded,
        countDocumentsWithFiles: countDocumentsWithUploadedFiles(
          editableDocumentationRequired
        ),
        countAllDocuments:
          editableDocumentationRequired?.required_files?.length,
      }));
    }
  };

  const addFilesToDocumentation = (documentation, files) => {
    const documentationFromFiles = files[0].document;
    const documentationRequired = { ...documentation };
    documentationRequired.required_files =
      documentationRequired.required_files.map((requiredFiles) => {
        if (requiredFiles.name === documentationFromFiles.name) {
          return {
            ...requiredFiles,
            status: documentStatus.UPLOADED,
            uploaded_files: [
              ...requiredFiles.uploaded_files,
              ...files.map((file) => {
                return { name: file.name, mime_type: file.mime_type };
              }),
            ],
          };
        }
        return requiredFiles;
      });
    let areRequiredFilesUploaded = true;
    documentationRequired.required_files.map((requiredFiles) => {
      if (!UPLOADED_FILE_STATUS.includes(requiredFiles.status)) {
        areRequiredFilesUploaded = false;
      }
    });
    return {
      ...documentationRequired,
      are_required_files_uploaded: areRequiredFilesUploaded,
    };
  };

  const addFile = (e, document) => {
    const value = e.target.files[0];
    if (value) {
      convertToBase64(value, (result) => {
        setState((s) => ({
          ...s,
          files: [
            ...s.files,
            {
              result,
              file: value,
              name: value.name,
              size: value.size,
              mime_type: value.type,
              document: document,
            },
          ],
        }));
      });
    }
  };

  const removeFile = (e, fileToRemove) => {
    const indexToRemove = [...state.files].findIndex(
      (file) => file.name === fileToRemove.name
    );
    const remainingFiles = [...state.files];
    remainingFiles.splice(indexToRemove, 1);

    setState((s) => ({
      ...s,
      files: remainingFiles,
    }));
  };

  const handleOpen = (e, documentation) => {
    setState((s) => ({
      ...s,
      open: true,
      document: documentation,
    }));
  };

  const handleClose = (e) => {
    setState((s) => ({
      ...s,
      fadeOut: true,
    }));
    setTimeout(
      () =>
        setState((s) => ({
          ...s,
          open: false,
          document: undefined,
          fadeOut: false,
          files: [],
        })),
      500
    );
  };

  const handleUpload = () => {
    if (files) {
      dispatch({
        type: "REQUEST_UPLOAD_BORROWER_DOCUMENTATION",
        payload: {
          id: editableDocumentationRequired.id,
          op: "processPatchUploadFileToDocumentationRequired",
          documentation_required_type: "borrower",
          files: files.map((file) => ({
            name: file.name,
            data: file.result,
            type: file.document.name,
          })),
        },
      });
      setState((s) => ({
        ...s,
        filesUploading: true,
      }));
    }
  };

  const handleConfirmDocuments = () => {
    setState((s) => ({
      ...s,
      confirmDocumentationClicked: true,
    }));
    getDocumentationRequiredByLoanIdAndBorrowerId();
  };

  const setFormSubmitSuccess = () => {
    dispatchModuleOK();
  };

  const onSkipForm = () => {
    dispatch({
      type: "SET_MODULE_SKIPPED",
    });
  };

  const dispatchModuleOK = () => {
    dispatch({
      type: "SET_FLOW_ITEM_OK",
    });
  };

  if (documentation === undefined || documentationSync === false) {
    return null;
  }

  return (
    <>
      <Box className="appear-anim">
        <IconTitle
          icon={
            <ThemedAsset
              id={assetIds.ICON_CLOUD_UPLOAD}
              width={ICON_TITLE_HEIGHT}
              height={ICON_TITLE_HEIGHT}
              style={{ color: palette.secondary.main }}
            />
          }
          title={<Text tId={"DOCUMENTATION"} />}
          subtitle={<Text tId="DOCUMENTATION_UPLOAD" />}
        />
        <Box className="appear-anim mt-3" style={{ overflowY: "auto" }}>
          <Grid container>
            {documentation?.required_files?.map((document) => (
              <Grid item key={document.id} xs={12}>
                <DocumentationRequired
                  document={document}
                  handleOpen={handleOpen}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
      <Box>
        {areRequiredFilesUploaded ? (
          <>
            <InformationMessage
              variant={{
                type: "info",
                icon: (
                  <i
                    className="material-icons-outlined"
                    style={{ fontSize: 20 }}
                  >
                    error_outline
                  </i>
                ),
              }}
              isScreenXs={true}
              messageContent={{
                body: <Text tId="VALIDATE_DOCUMENTATION_WARNING" />,
              }}
            />
            <MainButton onClick={handleConfirmDocuments}>
              <Text tId={"VALIDATE_DOCUMENTATION"} />
            </MainButton>
          </>
        ) : (
          <MainButton disabled={true}>
            <Text
              tId="PENDING_DOCUMENTATION"
              dynamicDataTexts={[
                `${countDocumentsWithFiles}/${countAllDocuments}`,
              ]}
            />
          </MainButton>
        )}
      </Box>
      <DocumentRequiredDialog
        files={files}
        open={open}
        fadeOut={fadeOut}
        onClose={handleClose}
        onSubmit={handleUpload}
        addFile={addFile}
        removeFile={removeFile}
        documentRequired={document}
        filesUploading={filesUploading}
      />
    </>
  );
}
